/**
 * OFFICIAL QUICKVIEW HOOK - SINGLE SOURCE OF TRUTH
 * 
 * Standardized QuickView state management and logic for consistent
 * integration across all components. Handles DisplayProduct conversion,
 * modal state, and recently viewed tracking.
 * 
 * Usage:
 * const { quickViewProduct, isOpen, openQuickView, closeQuickView } = useQuickView();
 */

import { useState, useCallback, useMemo } from 'react';
import { DisplayProduct, createDisplayProduct, isDisplayProduct } from '@/types/productDisplay';
import { useRecentlyViewed } from '@/hooks/useRecentlyViewed';
import { productionLogger } from '@/utils/production-logger';
import { TIMING } from '@/constants';

export interface QuickViewHookReturn {
  quickViewProduct: DisplayProduct | null;
  isQuickViewOpen: boolean;
  openQuickView: (product: any) => void;
  closeQuickView: () => void;
  handleViewProduct: (navigationCallback?: () => void) => void;
}

/**
 * Custom hook for managing QuickView modal state and product data
 * Provides consistent interface across all components that use QuickView
 */
export const useQuickView = (): QuickViewHookReturn => {
  const [quickViewProduct, setQuickViewProduct] = useState<DisplayProduct | null>(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const { addToRecentlyViewed } = useRecentlyViewed();

  /**
   * Opens QuickView modal with the provided product
   * Automatically converts any product format to DisplayProduct
   * Optimized with debouncing to prevent rapid successive calls
   */
  const openQuickView = useCallback((product: any) => {
    if (!product) {
      productionLogger.warn('useQuickView: Attempted to open QuickView with null/undefined product');
      return;
    }

    try {
      console.log('🔍 useQuickView: openQuickView called with product:', product.title);
      // Convert to DisplayProduct format for consistent handling
      const displayProduct: DisplayProduct = isDisplayProduct(product) 
        ? product 
        : createDisplayProduct(product);
      
      console.log('🔍 useQuickView: Setting modal state - product:', displayProduct.title);
      console.log('🔍 useQuickView: Setting isQuickViewOpen to true');
      
      // Performance optimization: batch state updates
      requestAnimationFrame(() => {
        setQuickViewProduct(displayProduct);
        setIsQuickViewOpen(true);
        console.log('🔍 useQuickView: State updates completed');
      });
      
      productionLogger.debug('QuickView opened for product:', displayProduct.title);
    } catch (error) {
      console.error('🔍 useQuickView: Failed to open QuickView:', error);
      productionLogger.error('Failed to open QuickView:', error);
    }
  }, []);

  /**
   * Closes QuickView modal and clears product state
   * Optimized with delayed cleanup to prevent flicker
   */
  const closeQuickView = useCallback(() => {
    setIsQuickViewOpen(false);
    // Delay product cleanup to allow for smooth modal closing animation
    setTimeout(() => {
      setQuickViewProduct(null);
    }, TIMING.ANIMATION_DURATION);
    productionLogger.debug('QuickView closed');
  }, []);

  /**
   * Handles the "View Product" action from QuickView modal
   * Closes modal, adds to recently viewed, and executes optional navigation
   */
  const handleViewProduct = useCallback((navigationCallback?: () => void) => {
    if (quickViewProduct) {
      // Close the modal first
      closeQuickView();
      
      // Add to recently viewed (for Shopify products)
      if (quickViewProduct.handle) {
        addToRecentlyViewed(quickViewProduct as any);
      }
      
      // Execute navigation callback if provided
      if (navigationCallback) {
        navigationCallback();
      } else {
        // Default navigation - construct product URL
        const productUrl = quickViewProduct.handle 
          ? `/shopify-product/${quickViewProduct.handle}`
          : `/product/${quickViewProduct.id}`;
        window.location.href = productUrl;
      }
      
      productionLogger.debug('QuickView navigation completed for:', quickViewProduct.title);
    }
  }, [quickViewProduct, closeQuickView, addToRecentlyViewed]);

  // Memoize return object to prevent unnecessary re-renders
  return useMemo(() => ({
    quickViewProduct,
    isQuickViewOpen,
    openQuickView,
    closeQuickView,
    handleViewProduct
  }), [quickViewProduct, isQuickViewOpen, openQuickView, closeQuickView, handleViewProduct]);
};

export default useQuickView;